import type { TFunction } from "i18next";
import { toDateOnlyISO } from "@/utils/datetime";
import type { CompareTranslationKey } from "./columnIds";
import { DELTA_IMPACT_COLUMN_SUFFIX } from "./columnIds";
import { isSelectionSendable } from "./periods";
import type {
  CompareImpact,
  ComparePeriod,
  CompareToSelection
} from "./types";

/**
 * The Compare-to fields a `FilterRequest` accepts, spelled as they travel.
 *
 * `FilterRequest` is a `CamelModel`, so a mis-cased key (`compare_period`) is
 * dropped by the backend without an error. Declaring the shape makes that a
 * compile error where the key is written.
 *
 * A `type` rather than an `interface`: only a type alias gets the implicit
 * index signature that a `Record<string, unknown>` slot requires.
 */
export type CompareFilterParams = {
  comparePeriod: ComparePeriod;
  /** ISO date (`YYYY-MM-DD`). Present only when the period is `custom`. */
  compareCustomStart?: string;
};

/**
 * The selection as request fields, or `undefined` when it is not complete
 * enough to send (a custom period with no start is rejected with a 400).
 */
export const buildCompareFilterParams = (
  selection: CompareToSelection | undefined
): CompareFilterParams | undefined => {
  if (!isSelectionSendable(selection)) return undefined;
  return {
    comparePeriod: selection.period,
    ...(selection.period === "custom" && selection.customStart
      ? { compareCustomStart: toDateOnlyISO(selection.customStart) }
      : {})
  };
};

/**
 * Every raw value the export can write in an Impact column. All five are
 * labelled so no grain writes a bare `noChange` into the file, whichever of
 * them its backend actually emits.
 */
export const COMPARE_IMPACT_VALUES = [
  "new",
  "churn",
  "upsell",
  "downsell",
  "noChange"
] as const satisfies readonly CompareImpact[];

/**
 * Raw impact value → the label the grid's Impact cell shows, for every Impact
 * column given. Uses the same keys as `ImpactCell`, so the file reads the way
 * the table does.
 */
export const buildImpactValueLabels = (
  impactColumnIds: readonly string[],
  t: TFunction,
  translationKey: CompareTranslationKey
): Record<string, Record<string, string>> => {
  const labels = Object.fromEntries(
    COMPARE_IMPACT_VALUES.map((impact) => [
      impact,
      t(`${translationKey}.impactValues.${impact}`)
    ])
  );
  return Object.fromEntries(
    impactColumnIds.map((columnId) => [columnId, { ...labels }])
  );
};

export interface CompareExportConfig {
  /** Whether the Compare-to feature is on for this viewer. */
  enabled: boolean;
  /** The period selected on screen. */
  selection: CompareToSelection | undefined;
  /**
   * Every Δ and Impact column id the table can ever render, on either
   * currency basis. Whatever is not exportable right now is excluded.
   */
  allColumnIds: readonly string[];
  /**
   * The Δ and Impact ids the table renders now (the active basis). Ignored
   * while `enabled` is false: nothing is exportable then.
   */
  exportableColumnIds: readonly string[];
  translationKey: CompareTranslationKey;
  t: TFunction;
}

export interface CompareExport {
  /** Ids to add to the table's `excludeFromExport`. */
  excludedColumnIds: readonly string[];
  /** Impact labels, keyed by UI column id, for `getExtraColumnValueLabels`. */
  impactValueLabels: Record<string, Record<string, string>>;
  /**
   * For `getExtraFilterParams`: the compare fields, sent only when the export
   * actually carries a Δ or Impact column, so the backend never computes a
   * comparison the file has no column for.
   */
  getFilterParams: (
    exportedColumnIds: ReadonlySet<string>
  ) => CompareFilterParams | undefined;
  /**
   * For `getColumnLabelOverrides`: the live headers of the exportable Δ and
   * Impact columns, picked from the table's current column list. A Δ header
   * names the selected period (`Δ ARR (MTD)`), and the label saved in
   * `columnsState` is a snapshot from whichever period was selected when the
   * layout was seeded — exporting it would title the file's column with a
   * period its figures were not computed for.
   */
  pickColumnLabels: (
    columns: readonly { id: string; label: string }[]
  ) => Record<string, string>;
}

/**
 * What a table passes to `useEntityExport` for its Compare-to columns.
 *
 * The UI ids need no remapping: `arrDeltaImpact` decamelizes to the backend's
 * `arr_delta_impact`, and `totalContractValueLocalDelta` to
 * `total_contract_value_local_delta`, which is exactly how contractsapi keys
 * them. A grain whose ids drift from that would map them through
 * `exportColumnIdMap` like any other column.
 */
export const buildCompareExport = ({
  enabled,
  selection,
  allColumnIds,
  exportableColumnIds,
  translationKey,
  t
}: CompareExportConfig): CompareExport => {
  const exportable = new Set(enabled ? exportableColumnIds : []);
  const filterParams = enabled ? buildCompareFilterParams(selection) : undefined;
  return {
    excludedColumnIds: allColumnIds.filter((id) => !exportable.has(id)),
    impactValueLabels: buildImpactValueLabels(
      [...exportable].filter((id) => id.endsWith(DELTA_IMPACT_COLUMN_SUFFIX)),
      t,
      translationKey
    ),
    getFilterParams: (exportedColumnIds) => {
      if (!filterParams) return undefined;
      for (const id of exportedColumnIds) {
        if (exportable.has(id)) return filterParams;
      }
      return undefined;
    },
    pickColumnLabels: (columns) =>
      Object.fromEntries(
        columns
          .filter((column) => exportable.has(column.id))
          .map((column) => [column.id, column.label])
      )
  };
};
